import { useEffect, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  Building2,
  Check,
  ChevronsUpDown,
  FlaskConical,
  LogOut,
  Menu,
  Monitor,
  Moon,
  Plus,
  Search,
  Sparkles,
  Sun,
  type LucideIcon,
} from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  Button,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Kbd,
  LogoMark,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@atrix-analytics/ui";
import { USE_FIXTURES } from "../api";
import { projectsQuery } from "../api/queries";
import { ENVIRONMENTS, type EnvironmentName } from "../api/schemas";
import { INSIGHT_TABS, SECTIONS, type Section } from "../nav";
import { useTheme, type ThemePref } from "../lib/theme";
import { useWorkspace } from "../lib/workspace";
import { RegionBadge } from "./region-badge";
import { useSignOut } from "./use-sign-out";
import { VerifyBanner } from "./verify-banner";

const ENV_LABEL: Record<EnvironmentName, string> = {
  production: "Production",
  preview: "Preview",
  development: "Development",
};
/** Whole-word short forms for narrow screens (a sliced label reads as a typo: "Deve"). */
const ENV_SHORT: Record<EnvironmentName, string> = { production: "Prod", preview: "Preview", development: "Dev" };

export function AppShell({ children }: { children: ReactNode }) {
  const [navOpen, setNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => setNavOpen(false), [pathname]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-screen bg-bg">
      <a
        href="#main"
        className="sr-only z-50 rounded-md bg-accent px-3 py-2 text-accent-fg focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
      >
        Skip to content
      </a>
      <TopBar onMenu={() => setNavOpen(true)} onPalette={() => setPaletteOpen(true)} />
      <div className="flex">
        <aside className="sticky top-14 hidden h-[calc(100vh-3.5rem)] w-56 shrink-0 overflow-y-auto border-r border-border lg:block">
          <SideNav />
        </aside>
        <main id="main" className="min-w-0 flex-1 px-4 py-5 sm:px-6 lg:px-8 lg:py-7">
          <VerifyBanner />
          {children}
        </main>
      </div>

      <Dialog open={navOpen} onOpenChange={setNavOpen}>
        <DialogContent
          aria-describedby={undefined}
          className="top-0 left-0 h-full content-start w-[280px] max-w-[85vw] translate-x-0 translate-y-0 gap-0 overflow-y-auto rounded-none border-y-0 border-l-0 p-0 data-[state=open]:slide-in-from-left data-[state=open]:zoom-in-100"
        >
          <DialogTitle className="flex h-14 items-center gap-2 border-b border-border px-4 text-sm font-semibold">
            <LogoMark size={20} /> Navigation
          </DialogTitle>
          <SideNav />
        </DialogContent>
      </Dialog>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}

function TopBar({ onMenu, onPalette }: { onMenu: () => void; onPalette: () => void }) {
  const { project, region, environment, setEnvironment } = useWorkspace();
  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-1.5 border-b border-border bg-bg/90 px-2 backdrop-blur sm:gap-2 sm:px-4">
      <Button variant="ghost" size="icon" className="lg:hidden" onClick={onMenu} aria-label="Open navigation">
        <Menu />
      </Button>
      <Link to="/p/$projectId" params={{ projectId: project.id }} className="hidden rounded-md p-1 sm:block" aria-label="Project home">
        <LogoMark size={22} />
      </Link>
      <WorkspaceSwitcher />
      <EnvironmentSwitcher value={environment} onChange={setEnvironment} />
      <RegionBadge code={project.region} region={region} />
      {USE_FIXTURES && <DemoBadge />}
      <div className="flex-1" />
      <button
        type="button"
        onClick={onPalette}
        className="flex h-8.5 items-center gap-2 rounded-md border border-border-strong bg-bg-raised px-2.5 text-sm text-fg-faint transition-colors hover:text-fg-muted sm:w-56 md:w-64"
        aria-label="Ask or jump to… (Command K)"
      >
        <Search className="size-4" aria-hidden />
        <span className="hidden flex-1 text-left sm:inline">Ask or jump to…</span>
        <Kbd className="hidden sm:inline-flex">⌘K</Kbd>
      </button>
      <ThemeMenu />
      <UserMenu />
    </header>
  );
}

/** Visible at every width: simulated numbers must never be mistaken for a real project's. */
function DemoBadge() {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="flex h-7 shrink-0 items-center gap-1 rounded-md border border-warn/40 bg-warn/10 px-1.5 text-xs font-medium text-warn"
          aria-label="Demo mode: all data is simulated in this browser"
        >
          <FlaskConical aria-hidden className="size-3.5" />
          <span className="hidden md:inline">Demo data</span>
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-60">
        Demo mode. Every number here is simulated in this browser and nothing is sent to a server.
      </TooltipContent>
    </Tooltip>
  );
}

function WorkspaceSwitcher() {
  const { me, org, project } = useWorkspace();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const projects = useQuery(projectsQuery(org.id));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-8.5 min-w-0 items-center gap-1.5 rounded-md px-2 text-sm hover:bg-bg-overlay"
          aria-label={`Organization ${org.name}, project ${project.name}. Switch project`}
        >
          <span className="hidden max-w-32 truncate text-fg-muted md:inline">{org.name}</span>
          <span className="hidden text-fg-faint md:inline" aria-hidden>
            /
          </span>
          <span className="max-w-[7.5rem] truncate font-medium sm:max-w-48">{project.name}</span>
          <ChevronsUpDown className="size-3.5 shrink-0 text-fg-faint" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Projects in {org.name}</DropdownMenuLabel>
        {projects.isPending && <DropdownMenuItem disabled>Loading…</DropdownMenuItem>}
        {projects.data?.map((p) => (
          <DropdownMenuItem
            key={p.id}
            onSelect={() => navigate({ to: "/p/$projectId", params: { projectId: p.id } })}
          >
            <span className="flex-1 truncate">{p.name}</span>
            <span className="font-mono text-[10px] text-fg-faint uppercase">{p.region}</span>
            {p.id === project.id && <Check className="size-4 text-accent" aria-label="Current" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem onSelect={() => navigate({ to: "/onboarding/project", search: { org: org.id } })}>
          <Plus /> New project
        </DropdownMenuItem>
        {me.orgs.length > 1 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Organizations</DropdownMenuLabel>
            {me.orgs
              .filter((o) => o.id !== org.id)
              .map((o) => (
                <DropdownMenuItem
                  key={o.id}
                  onSelect={async () => {
                    const first = (await qc.fetchQuery(projectsQuery(o.id)))[0];
                    await (first
                      ? navigate({ to: "/p/$projectId", params: { projectId: first.id } })
                      : navigate({ to: "/onboarding/project", search: { org: o.id } }));
                  }}
                >
                  <Building2 /> <span className="truncate">{o.name}</span>
                </DropdownMenuItem>
              ))}
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate({ to: "/onboarding/org" })}>
          <Plus /> New organization
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function EnvironmentSwitcher({ value, onChange }: { value: EnvironmentName; onChange: (e: EnvironmentName) => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-border-strong px-2 text-xs font-medium hover:bg-bg-overlay"
          aria-label={`Environment: ${ENV_LABEL[value]}. Switch environment`}
        >
          <span
            aria-hidden
            className={cn("size-1.5 rounded-full", value === "production" ? "bg-ok" : value === "preview" ? "bg-warn" : "bg-fg-faint")}
          />
          <span className="hidden sm:inline">{ENV_LABEL[value]}</span>
          <span className="sm:hidden">{ENV_SHORT[value]}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>Environment</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={value} onValueChange={(v) => onChange(v as EnvironmentName)}>
          {ENVIRONMENTS.map((e) => (
            <DropdownMenuRadioItem key={e} value={e}>
              {ENV_LABEL[e]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <p className="px-2 pt-1 pb-1.5 text-xs text-fg-muted">Each environment has its own key and its own data.</p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const THEME_ICON: Record<ThemePref, LucideIcon> = { system: Monitor, light: Sun, dark: Moon };

function ThemeMenu() {
  const { pref, setPref } = useTheme();
  const Icon = THEME_ICON[pref];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={`Theme: ${pref}`} className="hidden sm:inline-flex">
          <Icon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup value={pref} onValueChange={(v) => setPref(v as ThemePref)}>
          <DropdownMenuRadioItem value="system">System</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="light">Light</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">Dark</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu() {
  const { me, project } = useWorkspace();
  const { pref, setPref } = useTheme();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const initials = (me.user.name || me.user.email)
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="rounded-full" aria-label={`Account menu for ${me.user.name || me.user.email}`}>
          <Avatar className="size-8">
            <AvatarFallback className="text-xs">{initials}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="font-normal">
          <span className="block truncate text-sm font-medium text-fg">{me.user.name}</span>
          <span className="block truncate text-xs text-fg-muted">{me.user.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate({ to: "/p/$projectId/settings/account", params: { projectId: project.id } })}>
          Account and security
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate({ to: "/p/$projectId/settings/tokens", params: { projectId: project.id } })}>
          Personal access tokens
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="sm:hidden">Theme</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={pref} onValueChange={(v) => setPref(v as ThemePref)} className="sm:hidden">
          <DropdownMenuRadioItem value="system">System</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="light">Light</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">Dark</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator className="sm:hidden" />
        <DropdownMenuItem onSelect={signOut}>
          <LogOut /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SideNav() {
  const { project } = useWorkspace();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const base = `/p/${project.id}`;
  const isActive = (s: Section) =>
    s.key === "home" ? pathname === base || pathname === `${base}/` || pathname === `${base}/install` : pathname.startsWith(`${base}/${s.key}`);

  return (
    <nav aria-label="Main" className="flex flex-col gap-0.5 p-2">
      {SECTIONS.map((s) => {
        const active = isActive(s);
        const Icon = s.icon;
        return (
          <Link
            key={s.key}
            to={s.key === "home" ? "/p/$projectId" : (`/p/$projectId/${s.key}` as "/p/$projectId")}
            params={{ projectId: project.id }}
            aria-current={active ? "page" : undefined}
            className={cn(
              "group flex h-8.5 items-center gap-2.5 rounded-md px-2.5 text-sm transition-colors",
              active ? "bg-bg-overlay font-medium text-fg" : "text-fg-muted hover:bg-bg-overlay/60 hover:text-fg",
              s.key === "settings" && "mt-3",
            )}
          >
            <Icon aria-hidden className={cn("size-4 shrink-0", s.ai ? "text-ai" : active ? "text-fg" : "text-fg-faint group-hover:text-fg-muted")} />
            <span className="flex-1">{s.label}</span>
            {!s.live && <span className="text-[10px] font-medium tracking-wide text-fg-faint uppercase">Soon</span>}
          </Link>
        );
      })}
    </nav>
  );
}

function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { project } = useWorkspace();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);
  const go = (to: string) => {
    onOpenChange(false);
    void navigate({ to: to as "/p/$projectId", params: { projectId: project.id } });
  };
  const ask = (q: string) => {
    onOpenChange(false);
    void navigate({ to: "/p/$projectId/ask", params: { projectId: project.id }, search: q.trim() ? { q: q.trim() } : {} });
  };
  const question = query.trim();
  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} title="Ask or jump to">
      <CommandInput placeholder="Ask a question or jump to a page…" aria-label="Ask a question or search pages" value={query} onValueChange={setQuery} />
      <CommandList>
        <CommandEmpty>No matching page. Press Enter on “Ask” to ask it instead.</CommandEmpty>
        <CommandGroup heading="Ask" forceMount>
          {question.length > 0 ? (
            <CommandItem value={`ask ${question}`} keywords={[question]} onSelect={() => ask(question)} forceMount>
              <Sparkles aria-hidden className="size-4 text-ai" />
              <span className="min-w-0 flex-1 truncate">
                Ask <span className="text-ai">“{question}”</span>
              </span>
              <span className="ml-auto text-xs text-fg-faint">Enter</span>
            </CommandItem>
          ) : (
            <CommandItem value="ask a question about your data" onSelect={() => ask("")}>
              <Sparkles aria-hidden className="size-4 text-ai" />
              <span className="text-ai">Ask a question about your data</span>
            </CommandItem>
          )}
        </CommandGroup>
        <CommandGroup heading="Insights">
          {INSIGHT_TABS.filter((t) => t.live).map((t) => (
            <CommandItem key={t.key} value={`insights ${t.label}`} onSelect={() => go(`/p/$projectId/insights/${t.key}`)}>
              {t.label}
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandGroup heading="Go to">
          {SECTIONS.map((s) => (
            <CommandItem key={s.key} value={s.label} onSelect={() => go(s.key === "home" ? "/p/$projectId" : `/p/$projectId/${s.key}`)}>
              <s.icon aria-hidden className={cn("size-4", s.ai ? "text-ai" : "text-fg-faint")} />
              {s.label}
            </CommandItem>
          ))}
          <CommandItem value="install sdk keys snippet" onSelect={() => go("/p/$projectId/install")}>
            Install the SDK
          </CommandItem>
          <CommandItem value="personal access tokens" onSelect={() => go("/p/$projectId/settings/tokens")}>
            Personal access tokens
          </CommandItem>
          <CommandItem value="account password sessions security" onSelect={() => go("/p/$projectId/settings/account")}>
            Account and security
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
